import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';

import Notification from '@tds/core-notification';
import Spinner from '@tds/core-spinner';
import Button from '@tds/core-button';

import { Error } from '../design/elements';
import RichText from '../richText';
import FormStep from './formStep';
import FormStepContactCentre from './formStepContactCentre';
import FormStepBusinessApp from './formStepBusinessApp';
import FormStepIoT from './formStepIoT';

import { FormWrapper, FormTitle } from './css';
import submitPayload, { isEligibilityForm } from './formSubmitLogic';
import {
  businessConnectContactCentreOnlineTool,
  businessConnectContactCentreLandingPage,
  businessConnectQuoteToolForm,
  orderDetailsForm,
  fieldMultisiteOrder,
  fieldNumberOfMultiSites,
  multisiteUniqueFields,
  orderStatus,
  quoteStatus,
  businessAppsCategoryName,
  constactCentreCategoryName,
  businessAppsOrderForm,
  iotCategoryName,
  iotOrderForm,
  iotPrequalificationForm,
  businessAppsPrequalificationForm,
  fieldIotActivationOrRenwal,
  fieldIotProductSelected
} from '../utils/constant';
import request, {
  isAdmin,
  isProposalHouse,
  isOrderFulfillmentTeam,
  isSalesSupport,
  isSalesManager,
  isSalesRep
} from '../utils/request';
import { getPathVariables } from '../misc';
import { useLocalizedStringsContext } from '../context/LocalizedStringsContext';

import {
  viewPDF,
  viewDocx,
  movedToQuoteApproved,
  sendToSRT,
  submitForApproval,
  onSave,
  onSaveAndExit,
  onOrderCancelled,
  onCallcentreFormSubmit,
  onBusinessAppsFormSubmit
} from './utils/formActions';
import Dashboards from './dashboards';
import ConfirmationBox from '../design/shared/ConfirmationBox';

const Form = ({ data, orderId, history, initialStep }) => {
  if (!data) return <Error text="Error!" />;
  const localizedStrings = useLocalizedStringsContext();
  const {
    contentfulValues: {
      yes,
      valueGeotab,
      valueFleetcomplete,
      valueRaven,
      valueActivation,
      valueRenewal
    }
  } = localizedStrings;

  const {
    fields: { description, steps, skipIntro, hideProgressBar, showOrdersList }
  } = data;
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState();
  const [orderData, setOrderData] = useState();
  const [eligible, setEligible] = useState({
    isEligible: false,
    note: {}
  });
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [redirectUrl, setRedirectUrl] = useState('');

  const {
    location: { pathname }
  } = history;
  const [locale, categoryName, formName] = getPathVariables(pathname);

  const isMultisiteOrder = _.get(orderData, fieldMultisiteOrder, '') === yes;
  const totalNumberOfMultisites = +_.get(orderData, fieldNumberOfMultiSites, 0);
  const currentSite = _.get(orderData, 'currentSite', 1);
  let isSalesRepSectionVisible = false;
  if (formName === businessAppsPrequalificationForm) {
    isSalesRepSectionVisible = isAdmin || isSalesRep;
  } else {
    isSalesRepSectionVisible = isAdmin || isSalesRep || isSalesSupport;
  }

  const updateStep = newStep => {
    setStep(newStep);
  };

  useEffect(() => {
    const newStep = (initialStep && Number(initialStep)) || (skipIntro ? 1 : 0);
    setStep(newStep);
  }, [initialStep]);

  useEffect(() => {
    if (orderId) {
      request.get(`/orders/${orderId}`).then(response => {
        if (response.data) {
          setOrderData(response.data);
          setLoading(false);
        }
      });
    } else setLoading(false);
  }, [orderId, initialStep]);

  const showNewOrder = isEligibilityForm();

  const removeMultiSiteUniqueKeys = formData => {
    const updatedOrderData = { ...orderData, ...formData };
    updatedOrderData.currentSite = currentSite + 1;
    updatedOrderData.quoteStatus = quoteStatus.incomplete;
    multisiteUniqueFields.forEach(key => {
      delete updatedOrderData[key];
    });
    return updatedOrderData;
  };

  const moveToNextSite = formData => {
    const newOrderData = removeMultiSiteUniqueKeys(formData);
    request.post('/orders', { ...newOrderData }).then(resp => {
      if (resp.data) {
        const {
          data: { id }
        } = resp;
        setOrderData(data);
        history.push({
          pathname: `/${locale}/${categoryName}/${orderDetailsForm}`,
          search: `?orderId=${id}&step=1`
        });
      }
    });
  };

  const createNewOrder = (type, formLink, formData = {}) => {
    const profile = request.getProfile();
    delete profile.id;
    delete profile.province;
    delete profile.status;
    delete profile.createdAt;
    delete profile.updatedAt;
    const payload = {
      ...profile,
      ...formData,
      status: orderStatus.incomplete,
      type,
      locale,
      categoryName,
      formName
    };
    request.post('/orders', payload).then(resp => {
      if (resp.data) {
        const {
          data: { id }
        } = resp;
        setOrderData(data);
        setStep(step + 1);
        history.push({
          pathname: `/${locale}/${categoryName}/${formLink}`,
          search: `?orderId=${id}`
        });
      }
    });
  };

  // Finish the flow
  const onFormSubmit = async formData => {
    const searchString = (paramStep, id = '') => {
      return orderData && orderData.id
        ? `?orderId=${orderData.id}&step=${paramStep}`
        : `?orderId=${id}&step=${paramStep}`;
    };

    // Send order data to API
    const response = await submitPayload(
      {
        ...orderData,
        ...formData,
        locale,
        categoryName,
        formName,
        step
      },
      step
    );

    setOrderData({ ...orderData, ...response.data });
    if (response) {
      if (formName === orderDetailsForm && step === 7) {
        updateStep(0);
        setEligible({ isEligible: false, note: {} });
        setOrderData();

        history.push({
          pathname: `/${locale}/${categoryName}/${businessConnectQuoteToolForm}`
        });
      } else {
        if (
          isMultisiteOrder &&
          step === steps.length - 1 &&
          currentSite !== totalNumberOfMultisites &&
          _.get(orderData, 'status', orderStatus.incomplete) !==
            orderStatus.waitingOnSales
        ) {
          moveToNextSite(formData);
          return;
        }
        const newStep = formName === orderDetailsForm ? step + 1 : step;
        updateStep(newStep);
        history.push({
          pathname: `/${locale}/${categoryName}/${orderDetailsForm}`,
          search: searchString(newStep, response.data.id)
        });
      }
    }
  };

  const onIoTFormSubmit = async formData => {
    setLoading(true);
    try {
      const payload = {
        ...orderData,
        ...formData,
        status: orderStatus.pendingValidation
      };
      if (payload.channels && payload.channels.length) {
        payload.channelId = payload.channels[0].id;
      }
      if (payload.iotCompanyName) {
        payload.legalBusinessName_businessPersonal = payload.iotCompanyName;
      }
      if (payload.iotPreferredLanguage) {
        payload.languageOfActivation = payload.iotPreferredLanguage;
      }
      const { id } = orderData;
      const result = await request.put(`/orders/${id}`, payload);
      if (result) {
        setLoading(false);
        setShowConfirmation(true);
        setRedirectUrl(
          `/${locale}/${iotCategoryName}/${iotPrequalificationForm}`
        );
      }
    } catch (err) {
      // .. Handle Error
    }
  };

  const prevStep = e => {
    e.preventDefault();
    updateStep(step - 1);
    history.push({
      pathname: `/${locale}/${categoryName}/${orderDetailsForm}`,
      search: `?orderId=${orderData.id}&step=${step - 1}`
    });
  };

  const prevStepBusinessOrderForm = e => {
    e.preventDefault();
    updateStep(step - 1);
    history.push({
      pathname: `/${locale}/${categoryName}/${businessAppsOrderForm}`,
      search: `?orderId=${orderData.id}&step=${step - 1}`
    });
  };

  const navigateToContactCentreHomePage = () => {
    history.push({
      pathname: `/${locale}/${categoryName}/${businessConnectContactCentreLandingPage}`
    });
  };

  const onRedirectUser = () => {
    if (!redirectUrl) return;
    setShowConfirmation(false);
    history.push({
      pathname: redirectUrl
    });
    window.location.reload();
  };

  const navigateToBusinessAppHomePage = () => {
    history.push({
      pathname: `/${locale}/${categoryName}/${businessAppsPrequalificationForm}`
    });
    window.location.reload();
  };

  /**
   * @returns all available dashboards
   * for the currently logged in user
   */
  const getAvailableDashboard = () => {
    const dashboards = [];
    /** Sales Rep Dashboard for Role Admin/Sales Rep/Sales Manager/Sales Support */
    if (
      formName === businessConnectQuoteToolForm &&
      step === 1 &&
      showOrdersList &&
      !isOrderFulfillmentTeam &&
      !isProposalHouse &&
      !isSalesManager
    )
      dashboards.push('SALES_REP_DASHBOARD');

    /** Call centre Sales Rep Dashboard for Role Admin/Sales Rep */
    if (formName === businessConnectContactCentreLandingPage)
      dashboards.push('CC_SALES_REP_DASHBOARD');

    /** IoT Sales Rep Dashboard for Role Admin/Sales Rep */
    if (
      formName === iotPrequalificationForm &&
      !isOrderFulfillmentTeam &&
      !isProposalHouse &&
      !isSalesManager
    )
      dashboards.push('IOT_SALES_REP_DASHBOARD');
    /** IoT Order Fulfillment Dashboard */
    if (
      formName === iotPrequalificationForm &&
      (isOrderFulfillmentTeam || isAdmin || isSalesSupport || isSalesManager)
    )
      dashboards.push('IOT_SOV_DASHBOARD');

    /** SOV Dashboard for Role Admin/Order Fulfillment */
    if (
      formName === businessConnectQuoteToolForm &&
      step === 1 &&
      showOrdersList &&
      (isOrderFulfillmentTeam || isAdmin || isSalesSupport || isSalesManager)
    )
      dashboards.push('SOV_DASHBOARD');

    /** Quote Approval Dashboard for Role Admin/Proposal House */
    if (
      formName === businessConnectQuoteToolForm &&
      step === 1 &&
      showOrdersList &&
      (isProposalHouse || isAdmin)
    )
      dashboards.push('QUOTE_APPROVAL_DASHBOARD');

    /** Business Apps Dashboard */
    if (
      formName === businessAppsPrequalificationForm &&
      (isAdmin || isSalesRep)
    )
      dashboards.push('BA_SALES_REP_DASHBOARD');

    if (
      formName === businessAppsPrequalificationForm &&
      step === 0 &&
      showOrdersList &&
      (isOrderFulfillmentTeam || isAdmin || isSalesManager || isSalesSupport)
    )
      dashboards.push('BA_ORDER_FULFILLMENT_DASHBOARD');

    return dashboards;
  };

  const getIoTFormStep = () => {
    if (formName === iotOrderForm) {
      const product = _.get(orderData, fieldIotProductSelected, '');
      const type = _.get(orderData, fieldIotActivationOrRenwal, '');

      if (product === valueGeotab && type === valueActivation) return steps[0];
      if (product === valueGeotab && type === valueRenewal) return steps[1];
      if (product === valueFleetcomplete && type === valueActivation)
        return steps[2];
      if (product === valueFleetcomplete && type === valueRenewal)
        return steps[3];
      if (product === valueRaven) return steps[4];
    }
    return steps[0];
  };

  const getFormStep = () => {
    if (categoryName === iotCategoryName)
      return (
        <FormStepIoT
          key={`formstep-${step}`}
          stepData={getIoTFormStep()}
          orderData={orderData}
          steps={steps}
          step={step}
          setEligible={setEligible}
          eligible={eligible}
          history={history}
          setOrderData={setOrderData}
          onSaveAndExit={fields =>
            onSaveAndExit(fields, orderData, step, history, setLoading)
          }
          onFormSubmit={onIoTFormSubmit}
          createNewIotOrder={formData =>
            createNewOrder('IOT', iotOrderForm, formData)
          }
          formName={formName}
          navigateToContactCentreHomePage={navigateToContactCentreHomePage}
        />
      );
    if (categoryName === businessAppsCategoryName)
      return (
        <FormStepBusinessApp
          key={`formstep-${step}`}
          stepData={steps[step - 1] ? steps[step - 1] : steps[0]}
          orderData={orderData}
          steps={steps}
          step={step}
          onViewDocx={fields => {
            viewDocx(orderData);
            movedToQuoteApproved(fields, orderData).then(() => {
              navigateToBusinessAppHomePage();
            });
          }}
          setEligible={setEligible}
          eligible={eligible}
          history={history}
          prevStep={prevStepBusinessOrderForm}
          onCancel={() => {
            onOrderCancelled(orderData);
            navigateToBusinessAppHomePage();
          }}
          setOrderData={setOrderData}
          onSaveAndExit={fields =>
            onSaveAndExit(fields, orderData, step, history, setLoading)
          }
          onFormSubmit={ordrData =>
            onBusinessAppsFormSubmit(
              ordrData,
              locale,
              categoryName,
              formName,
              step,
              setOrderData,
              updateStep,
              history
            )
          }
          createNewBusinessAppOrder={formData =>
            createNewOrder('BA', businessAppsOrderForm, formData)
          }
          formName={formName}
          navigateToContactCentreHomePage={navigateToContactCentreHomePage}
        />
      );
    if (categoryName === constactCentreCategoryName)
      return (
        <FormStepContactCentre
          key={`formstep-${step}`}
          stepData={steps[0]}
          orderData={orderData}
          setOrderData={setOrderData}
          onSaveAndExit={fields =>
            onSaveAndExit(fields, orderData, step, history, setLoading)
          }
          onFormSubmit={onCallcentreFormSubmit}
          createNewContactOrder={() =>
            createNewOrder('CC', businessConnectContactCentreOnlineTool)
          }
          formName={formName}
          navigateToContactCentreHomePage={navigateToContactCentreHomePage}
        />
      );
    return (
      <FormStep
        key={`formstep-${step}`}
        stepData={steps[step - 1]}
        steps={steps}
        step={step}
        setEligible={setEligible}
        eligible={eligible}
        updateStep={updateStep}
        history={history}
        orderData={orderData}
        onSaveAndExit={fields =>
          onSaveAndExit(fields, orderData, step, history, setLoading)
        }
        onSave={fields => onSave(fields, orderData)}
        onViewPdf={() => viewPDF(locale, orderData)}
        sendToSRT={fields => sendToSRT(fields, orderData)}
        submitForApproval={fields => submitForApproval(fields, orderData)}
        onFormSubmit={onFormSubmit}
        hideProgressBar={hideProgressBar}
        showNewOrder={showNewOrder}
        setOrderData={setOrderData}
        formName={formName}
        prevStep={prevStep}
        currentSite={currentSite}
        isMultisiteOrder={isMultisiteOrder}
        totalNumberOfMultisites={totalNumberOfMultisites}
      />
    );
  };

  return (
    <>
      <Spinner fullScreen label="Loading page..." spinning={loading} />
      {[
        businessConnectQuoteToolForm,
        businessAppsPrequalificationForm,
        iotPrequalificationForm
      ].includes(formName) && (
        <div className="container 85%">
          <div id="title">
            <div id="title-wrapper">
              <div style={{ padding: '2rem 0 0 0' }}>
                <h2>
                  <RichText data={description} />
                </h2>
              </div>
            </div>
          </div>
        </div>
      )}
      {!(formName === orderDetailsForm || isSalesRepSectionVisible) &&
        categoryName === businessAppsCategoryName && (
          <FormTitle>{steps[0].fields.name}</FormTitle>
        )}
      {(formName === orderDetailsForm || isSalesRepSectionVisible) && (
        <div className="container 85%">
          <div id="introduction">
            <div id="introduction-wrapper">
              <FormWrapper>
                {([
                  businessConnectQuoteToolForm,
                  businessAppsPrequalificationForm,
                  iotPrequalificationForm
                ].includes(formName) ||
                  (orderData && orderData.id)) &&
                  getFormStep()}
                {eligible.note && eligible.note.content && (
                  <Notification variant="error" copy="en">
                    <RichText data={eligible.note} />
                  </Notification>
                )}
              </FormWrapper>
            </div>
          </div>
        </div>
      )}

      <Dashboards
        availableDashboards={getAvailableDashboard()}
        location={history.location}
      />
      <ConfirmationBox
        open={showConfirmation}
        hideCloseButton
        setOpen
        header="Order Submmitted"
        onClose={() => setShowConfirmation(false)}
      >
        <p>Your order has been submitted successfully.</p>
        <div style={{ display: 'flex', marginTop: '1rem' }}>
          <Button onClick={onRedirectUser}>Start New Order</Button>
        </div>
      </ConfirmationBox>
    </>
  );
};

Form.propTypes = {
  data: PropTypes.shape({
    fields: PropTypes.object
  }).isRequired,
  orderId: PropTypes.string,
  initialStep: PropTypes.string,
  history: PropTypes.shape({
    push: PropTypes.func,
    location: PropTypes.shape({
      pathname: PropTypes.string
    })
  }).isRequired
};

Form.defaultProps = {
  orderId: '',
  initialStep: ''
};

export default Form;
